import React, { useState, useEffect } from 'react';
import { getTypeLabel, getTypeClass, getStatusLabel, getPrimaryLink, generateBibTeXEntry, Publication, fetchPublications } from '../data/publications';
import FeaturedPublication from '../components/FeaturedPublication';
import './Publications.css';

const Publications: React.FC = () => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'manual'>('newest');

  useEffect(() => {
    const loadPublications = async () => {
      try {
        setLoading(true);
        const pubs = await fetchPublications();
        setPublications(pubs);
        setError(null);
      } catch (err) {
        console.error('Error loading publications:', err);
        setError('Failed to load publications');
      } finally {
        setLoading(false);
      }
    };

    loadPublications();
  }, []);

  // Unpublished work sorts ahead of published work of the same year
  const statusRank = (pub: Publication) => (pub.status ? 1 : 0);

  const sortedPublications = [...publications].sort((a, b) => {
    if (sortOrder === 'newest') return b.year - a.year || statusRank(b) - statusRank(a);
    if (sortOrder === 'oldest') return a.year - b.year || statusRank(a) - statusRank(b);
    return 0;
  });


  if (loading) {
    return (
      <div className="publications">
        <div className="container">
          <header className="page-header">
            <h1>Publications</h1>
            <p>Loading publications...</p>
          </header>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="publications">
        <div className="container">
          <header className="page-header">
            <h1>Publications</h1>
            <p>Error: {error}</p>
          </header>
        </div>
      </div>
    );
  }

  return (
    <div className="publications">
      <div className="container">
        <header className="page-header">
          <h1>Publications</h1>
          <p>My research contributions in artificial intelligence and machine learning</p>
        </header>

        {publications.some(p => p.featured) && (
          <FeaturedPublication publication={publications.find(p => p.featured)!} />
        )}

        <div className="publications-controls">
          <div className="sort-controls">
            <span className="sort-label">Sort:</span>
            <button className={`sort-btn${sortOrder === 'newest' ? ' active' : ''}`} onClick={() => setSortOrder('newest')}>Newest First</button>
            <button className={`sort-btn${sortOrder === 'oldest' ? ' active' : ''}`} onClick={() => setSortOrder('oldest')}>Oldest First</button>
            <button className={`sort-btn${sortOrder === 'manual' ? ' active' : ''}`} onClick={() => setSortOrder('manual')}>Manual</button>
          </div>
        </div>

        <div className="publications-list">
          {sortedPublications.map((pub) => {
            const primaryLink = getPrimaryLink(pub);
            return (
            <div key={pub.id} className="publication-item">
              <div className="publication-header">
                <h3 className="publication-title">
                  {primaryLink
                    ? <a href={primaryLink} target="_blank" rel="noopener noreferrer">{pub.title}</a>
                    : pub.title}
                </h3>
                {pub.status && (
                  <span className="publication-status">{getStatusLabel(pub.status)}</span>
                )}
                <span className={getTypeClass(pub.type)}>
                  {getTypeLabel(pub.type)}
                </span>
              </div>

              <div className="publication-meta">
                <p className="publication-authors">{pub.authors}</p>
                <p className="publication-venue">
                  <em>{pub.venue}</em>
                  {pub.volume && <span> vol. {pub.volume}</span>}
                  {pub.pages && <span>, pp. {pub.pages}</span>}
                  {pub.location && <span>, {pub.location}</span>}
                  <span> ({pub.year})</span>
                </p>
              </div>

              {/* The featured card already shows this abstract */}
              {pub.abstract && !pub.featured && (
                <div className="publication-abstract">
                  <p>{pub.abstract}</p>
                </div>
              )}

              <div className="publication-links">
                {pub.pdf && (
                  <a
                    href={pub.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="publication-link publication-link-primary"
                  >
                    PDF
                  </a>
                )}
                {pub.doi && (
                  <a
                    href={`https://doi.org/${pub.doi}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="publication-link"
                  >
                    DOI
                  </a>
                )}
                {pub.arxiv && (
                  <a
                    href={`https://arxiv.org/abs/${pub.arxiv}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="publication-link"
                  >
                    arXiv
                  </a>
                )}
                {pub.url && (
                  <a
                    href={pub.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="publication-link"
                  >
                    {/* With our own PDF above, this is just the publisher's copy */}
                    {pub.pdf ? 'Publisher' : 'Full Text'}
                  </a>
                )}
                {!pub.url && !pub.pdf && !pub.doi && !pub.arxiv && primaryLink && (
                  <a
                    href={primaryLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="publication-link"
                  >
                    Full Text on ResearchGate
                  </a>
                )}
                <button 
                  className="publication-link"
                  onClick={() => {
                    // Copy BibTeX entry to clipboard
                    const bibtexEntry = generateBibTeXEntry(pub);
                    navigator.clipboard.writeText(bibtexEntry).then(() => {
                      alert('BibTeX entry copied to clipboard!');
                    });
                  }}
                >
                  BibTeX
                </button>
              </div>
            </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default Publications;
